import { axiosClient } from '../../../library/Service/axiosClient';

const base = () => `${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/role-capabilities`;

export type RoleRow = {
    id: number;
    roleCode: string;
    name: string;
    type: string;
    parentRole?: string;
    isSystem: boolean;
    isActive: boolean;
};

export type CapabilityRow = {
    id: number;
    capabilityKey: string;
    module: string;
    description: string | null;
    isActive: boolean;
};

export type SetGrantResponse = {
    changed: boolean;
    granted: boolean;
    roleId?: number;
    roleCode: string;
    capabilityKey: string;
};

export type RoleMappingResponse = {
    role: RoleRow;
    capabilities: CapabilityRow[];
    grantedCapabilityKeys: string[];
};

export function listRoles(includeInactive = true) {
    return axiosClient().get<{ roles: RoleRow[] }>(`${base()}/roles`, {
        params: { includeInactive: includeInactive ? '1' : '0' },
    });
}

export function createRole(body: { roleCode: string; name: string; type: 'INTERNAL' | 'EXTERNAL' }) {
    return axiosClient().post<{ role: RoleRow }>(`${base()}/roles`, body);
}

export function updateRole(id: number, body: Partial<{ name: string; type: 'INTERNAL' | 'EXTERNAL'; isActive: boolean }>) {
    return axiosClient().put<{ role: RoleRow }>(`${base()}/roles/${id}`, body);
}

export function deleteRole(id: number) {
    return axiosClient().delete<{ role: RoleRow }>(`${base()}/roles/${id}`);
}

export function listCapabilities(includeInactive = true) {
    return axiosClient().get<{ capabilities: CapabilityRow[] }>(`${base()}/capabilities`, {
        params: { includeInactive: includeInactive ? '1' : '0' },
    });
}

export function createCapability(body: { capabilityKey: string; module: string; description?: string }) {
    return axiosClient().post<{ capability: CapabilityRow }>(`${base()}/capabilities`, body);
}

export function updateCapability(
    id: number,
    body: Partial<{ module: string; description: string | null; isActive: boolean }>
) {
    return axiosClient().put<{ capability: CapabilityRow }>(`${base()}/capabilities/${id}`, body);
}

export function deleteCapability(id: number) {
    return axiosClient().delete<{ capability: CapabilityRow }>(`${base()}/capabilities/${id}`);
}

export function getRoleMapping(roleId: number) {
    return axiosClient().get<RoleMappingResponse>(`${base()}/roles/${roleId}/mapping`);
}

export function setGrant(payload: { roleId?: number; roleCode?: string; capabilityKey: string; granted: boolean }) {
    return axiosClient().put<SetGrantResponse>(`${base()}`, payload);
}

// axiosClient rejects with { isError, message, status, data } (data = the server body); a raw axios error has response.data
export function errorMessage(err: unknown): string {
    const e = err as {
        data?: { error?: { message?: string }; message?: string };
        response?: { data?: { error?: { message?: string }; message?: string } };
        message?: string;
    };
    return (
        e?.data?.error?.message ||
        e?.response?.data?.error?.message ||
        e?.response?.data?.message ||
        e?.data?.message ||
        e?.message ||
        'Request failed'
    );
}
